import React, { useState } from 'react';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const imagesCountOfEntries = (catalogNameEntries) => catalogNameEntries.reduce((count, entry) => count + entry.images.length, 0);
const imagesCountOfGroups = (groups) => groups.reduce((count, group) => count + imagesCountOfEntries(group.catalog_name_entries), 0);

// The main groups, groups and catalog names under the selected node: { mainGroupId, groupId, catalogName }, each
// optional below the one above it. No selection keeps them all.
export const selectedBranch = (mainGroups, selection) => {
  if (!selection) {
    return mainGroups;
  }

  return mainGroups
    .filter((mainGroup) => mainGroup.main_group_id === selection.mainGroupId)
    .map((mainGroup) => ({
      ...mainGroup,
      groups: mainGroup.groups
        .filter((group) => !selection.groupId || group.group_id === selection.groupId)
        .map((group) => ({
          ...group,
          catalog_name_entries: group.catalog_name_entries.filter((entry) => !selection.catalogName || entry.catalog_name === selection.catalogName),
        }))
        .filter((group) => group.catalog_name_entries.length > 0),
    }))
    .filter((mainGroup) => mainGroup.groups.length > 0);
};

const selectionOfCatalogName = (mainGroups, catalogName) => {
  for (const mainGroup of mainGroups) {
    for (const group of mainGroup.groups) {
      if (group.catalog_name_entries.some((entry) => entry.catalog_name === catalogName)) {
        return { mainGroupId: mainGroup.main_group_id, groupId: group.group_id, catalogName };
      }
    }
  }
  return null;
};

const sameSelection = (first, second) => (
  !!first && !!second
    && first.mainGroupId === second.mainGroupId && first.groupId === second.groupId && first.catalogName === second.catalogName
);

// The product images' main groups, groups and catalog names as a tree under the menu's Product images entry.
const ProductImagesTree = ({ mainGroups, selection, onSelect }) => {
  const [collapsedKeys, setCollapsedKeys] = useState([]);

  const toggle = (key) => {
    setCollapsedKeys((previous) => (previous.includes(key) ? previous.filter((collapsedKey) => collapsedKey !== key) : [...previous, key]));
  };

  const renderNode = ({ key, label, count, depth, nodeSelection, children }) => {
    const collapsed = collapsedKeys.includes(key);
    const isCurrent = sameSelection(selection, nodeSelection);
    return (
      <li key={key}>
        <div className={styles.treeRow}>
          {children && (
            <button
              type="button"
              className={styles.treeToggle}
              style={{ left: `${depth * 16}px` }}
              aria-expanded={!collapsed}
              aria-label={label}
              onClick={() => toggle(key)}
            >
              <i className={`fa ${collapsed ? 'fa-caret-right' : 'fa-caret-down'}`} aria-hidden="true"></i>
            </button>
          )}
          <a
            href={`#${key}`}
            className={isCurrent ? 'dct-menu__link is-current' : 'dct-menu__link'}
            style={{ paddingLeft: `${18 + depth * 16}px` }}
            aria-current={isCurrent ? 'page' : undefined}
            onClick={(event) => {
              event.preventDefault();
              onSelect(nodeSelection);
            }}
            data-testid="product-images-tree-node"
          >
            {label}
            <span className="dct-menu__count"> ({count})</span>
          </a>
        </div>
        {children && !collapsed && <ul className={styles.treeList}>{children}</ul>}
      </li>
    );
  };

  return (
    <ul className={styles.treeList}>
      {mainGroups.map((mainGroup) => renderNode({
        key: `main-group-${mainGroup.main_group_id}`,
        label: mainGroup.main_group,
        count: imagesCountOfGroups(mainGroup.groups),
        depth: 1,
        nodeSelection: { mainGroupId: mainGroup.main_group_id },
        children: mainGroup.groups.map((group) => renderNode({
          key: `group-${group.group_id}`,
          label: group.group,
          count: imagesCountOfEntries(group.catalog_name_entries),
          depth: 2,
          nodeSelection: { mainGroupId: mainGroup.main_group_id, groupId: group.group_id },
          children: group.catalog_name_entries.map((entry) => renderNode({
            key: `catalog-name-${entry.catalog_name}`,
            label: entry.catalog_name,
            count: entry.images.length,
            depth: 3,
            nodeSelection: { mainGroupId: mainGroup.main_group_id, groupId: group.group_id, catalogName: entry.catalog_name },
          })),
        })),
      }))}
    </ul>
  );
};

// The tree under a menu's Product images entry, and the node selected in it, which ProductImages reports its list to
// and shows the branch of. While a node is selected, an upload selects the catalog name it went to, so the new image
// shows; a delete that empties the selected node shows all the images again.
export const useProductImagesSelection = () => {
  const [mainGroups, setMainGroups] = useState([]);
  const [selection, setSelection] = useState(null);

  const onListChange = (newMainGroups, uploadedCatalogName) => {
    setMainGroups(newMainGroups);
    setSelection((current) => {
      if (current && uploadedCatalogName) {
        return selectionOfCatalogName(newMainGroups, uploadedCatalogName);
      }
      return selectedBranch(newMainGroups, current).length > 0 ? current : null;
    });
  };

  const subtree = mainGroups.length > 0
    ? <ProductImagesTree mainGroups={mainGroups} selection={selection} onSelect={setSelection} />
    : undefined;

  return { selection, clearSelection: () => setSelection(null), onListChange, subtree };
};

export default ProductImagesTree;
